import * as React from "react";

import "./orders-submitted-table.scss";

import {faBan, faCaretDown, faCaretUp, faCircleInfo} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon}                             from "@fortawesome/react-fontawesome";
import {cx}                                          from "emotion";

import {AnyAction}                    from "@Common/react/any-action";
import {
    componentHelper as component,
    ComponentLink,
    Link,
    makeChain,
    valueHelper as value,
}                                     from "@Common/react/cst2";
import {Load2}                        from "@Common/react/load2";
import {UseState}                     from "@Common/react/use-state";
import {Types}                        from "@Common/types";
import {getPageSummary}               from "@Common/ui-components/heavy/common/design2/data-table/get-page-summary";
import {Pagination}                   from "@Common/ui-components/heavy/common/design2/data-table/pagination";
import {PageList}                     from "@Common/ui-components/heavy/common/design2/pagination-ui/page-list/page-list";
import {MEDIA_QUERY_MD, ViewportTest} from "@Common/ui-components/viewport-test";
import {sort as sorted}               from "@Common/utils/collections";

import {AGENT_LEVEL, COMPANY_LEVEL} from "@Xpresso/routes/common/logic/user-level";
import {consumeContext}             from "@Xpresso/loaders/consume-context";

import {OrderCard}                  from "../common/order-card";
import {chain} from "@Common/utils/fs";
import {cascadeMap} from "@Common/utils/cascade";
import {ConfirmDialog} from "@Common/ui-components/heavy/common/design2/dialogs/confirm-dialog/confirm-dialog";
import {AlertModal} from "@Common/ui-components/heavy/common/design2/dialogs/alert-modal/alert-modal";

const DELETE_INFO = "Clicking the trash icon will move the order from approval dashboard to pending orders";

type Row = {
    approvalType : Types.Apis.Payloads.Next.OrderApproval.GetAllApprovalRequests.ApprovalType,
    cart         : Types.Apis.Models.CartPendingApproval,
    legacy       : false,
    proofOrder   : false,
    order        : Types.Apis.Models.OrderPendingApproval,
} | {
    approvalType : Types.Apis.Payloads.Next.OrderApproval.GetAllApprovalRequests.ApprovalType,
    legacy       : true,
    proofOrder   : false,
    order        : Types.Apis.Models.StandaloneOrderPendingApproval,
} | {
    approvalType : Types.Apis.Payloads.Next.OrderApproval.GetAllApprovalRequests.ApprovalType,
    legacy       : false,
    proofOrder   : true,
    order        : Types.Apis.Models.OrderPendingApproval,
};

export const OrdersSubmittedTable = ({onRowDelete: handleRowDelete, rows, userLevel, sortState}:{
    onRowDelete : (row:Row) => Promise<void>,
    rows        : Row[],
    sortState   : Types.Common.React.UseState.UseStateNext<{id: string, ascending: boolean}>,
    userLevel   : Types.Apis.Models.UserLevel,
}) => (
    makeChain<JSX.Element>()
    .then("sortedRows", value(
        ({}) => {
            const sortedRows = sorted(rows, (row) => {
                let sortValue:any;
                switch (sortState.value.id) {
                    case "agentName"  : sortValue = row.order.agentName; break;

                    case "branchName" : sortValue = row.order.branchName; break;

                    case "cartId"     : sortValue = row.order.idCart; break;

                    case "date"       : sortValue = new Date(row.order.dateCreated).valueOf(); break;

                    case "orderId"    :
                    default           : sortValue = row.order.idOrder; break;
                }

                // always sort falsies last
                return (sortValue || (sortState.value.ascending ? String.fromCharCode(65535) : "")).toString();
            });
            return sortState.value.ascending ? sortedRows : sortedRows.reverse();
        }
    ))
    .then("pagination", ({sortedRows}, next:ComponentLink<typeof Pagination<Row>>) => (
        sortedRows && !(sortedRows.length > 5)
            ? next(null)
            : Pagination({
                pageSize: 5,
                rows: sortedRows,
                next,
            })
    ))
    .then("page", value(({pagination}) => pagination ? pagination.state.value : 0))
    .then(({pagination}, next:() => JSX.Element) => (
        <div className="orders-submitted-table-s7u">
            <div className="table">
                <div className="sub-table">
                    {next()}
                </div>
                {pagination && pagination.active && <div className="controls">
                    <div className="spacer"/>
                    {PageList(pagination)}
                    {getPageSummary({
                        pageIndex: pagination.state.value,
                        pageSize: 5,
                        count: rows.length
                    })}
                </div>}
            </div>
        </div>
    ))
    .then("paginatedRows", value(
        ({page, sortedRows}) => sortedRows.slice(page*5, page*5+5)
    ))
    .then(({paginatedRows}) => (
        <ViewportTest if={MEDIA_QUERY_MD} then={() => (
            makeChain<React.ReactNode>()
            .then("state", ({}, next:ComponentLink<typeof UseState<{buttonPosition?: {right: number, top: number}, contextRow?: Row}>>) => (
                UseState({next, initValue: {}})
            ))
            .then(({state: {onChange: setState, value: {buttonPosition, contextRow}}}) => (<>
                {buttonPosition && contextRow && (
                    <button
                        onClick={() => handleRowDelete(contextRow)}
                        style={buttonPosition}
                        className="mobile-delete"
                    >
                        <AnyAction action={() => setState({})}/>
                        <i><FontAwesomeIcon icon={faBan}/></i>
                        Delete
                    </button>
                )}
                {paginatedRows.map((row) => OrderCard({
                    details        : [
                        {label: "Program", value: row.order.programType?.toUpperCase() ?? "?"},
                        {label: "Type", value: row.order?.orderType ?? "?"},
                        {label: "Date", value: row.order?.dateCreated ? new Date(row.order.dateCreated).toLocaleDateString("en-US") : "?"},
                        ...(userLevel === COMPANY_LEVEL ? [{label: "Office", value: row.order?.branchName ?? "?"}] : []),
                        ...(userLevel !== AGENT_LEVEL   ? [{label: "User",   value: row.order?.agentName  ?? "?"}] : []),
                        ...("cart" in row ? [{label: "Cart ID", value: row.order.idCart}] : []),
                    ],
                    orderId        : row.order.idOrder.toString(),
                    orderName      : row.order.orderName ?? "CORRUPT ORDER",
                    onOptionsClick : ({currentTarget: {clientWidth, offsetLeft, offsetParent, offsetTop}}) => (
                        // timeout to queue after AnyAction
                        window.setTimeout(() => setState({
                            contextRow     : row,
                            buttonPosition : {
                                right : offsetParent.clientWidth - offsetLeft - clientWidth,
                                top   : offsetTop,
                            },
                        }), 0)
                    ),
                }))}
            </>))
            .run()
        )} else={() => {
            const columns = makeColumns({userLevel, onRowDelete: handleRowDelete});
            return (
                <table cellSpacing="0" cellPadding="0">
                    <colgroup>
                        {columns.map((col, idx) => (
                            <col key={idx} style={{width: col.width}}/>
                        ))}
                    </colgroup>
                    <thead><tr>
                        {columns.map((col, idx) => (
                            <th
                                key={idx}
                                onClick={col.sortId && (() => sortState.change(({ascending, id}) => ({ascending: col.sortId === id ? !ascending : true, id: col.sortId})))}
                                className={cx(col.className, {'sort-active': col.sortId === sortState.value.id, 'sortable': col.sortId})}
                                xd-qa-type="label"
                                xd-qa-name={col.xdQaName}
                            >
                                {col.label}&nbsp;{col.sortId && <FontAwesomeIcon icon={sortState.value.ascending ? faCaretUp : faCaretDown} className="sort-caret"/>}
                            </th>
                        ))}
                    </tr></thead>
                    <tbody>
                        {paginatedRows.map((row) => (
                            <tr key={row.order.idOrder} xd-qa-type="card" xd-qa-name={row.order.idOrder}>
                                {columns.map((col, index) => chain(
                                    col.renderRow(row),
                                    (render) => (
                                        <td
                                            key={index}
                                            title={typeof render === "string" || typeof render === "number" ? render.toString() : undefined}
                                            className={col.className}
                                            xd-qa-type="text"
                                            xd-qa-name={col.xdQaName}
                                        >{render}</td>
                                    )
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            );
        }}/>
    ))
    .run()
);

export const OrdersSubmittedState = ({next: rootNext, searchQuery, onSubmissionCancelled}:{
    next                   : Link<JSX.Element, {
        handleRowDelete : (row:Row) => Promise<void>,
        loading         : boolean,
        rows            : Row[],
        userLevel       : Types.Apis.Models.UserLevel,
    }>,
    onSubmissionCancelled ?: (order:Types.Apis.Models.OrderPendingApproval) => void,
    searchQuery           ?: string,
}) => (
    makeChain<any>()
    .then(...consumeContext("apis"))
    .then(...consumeContext("userLevels"))
    .then(...consumeContext("session"))
    .then("approvalRequestsLoad", ({apis, session}, next:ComponentLink<typeof Load2<ReturnType<typeof flattenApprovalRequests>>>) => (
        Load2({
            next,
            _key  : JSON.stringify({...session.value}),
            fetch : () => apis.next.approval.getPendingRequests().then(flattenApprovalRequests),
        })
    ))

    .then("alertModal", component(AlertModal)(({}, A) => A({})))
    .then("confirmationDialog", component(ConfirmDialog)(({}, D) => D({})))

    .then(({
        apis,
        approvalRequestsLoad: {onChange: setApprovalRequests, loading, value: approvalRequests},
        alertModal: {show: alert},
        confirmationDialog: {show: confirm},
        userLevels: {userLevel},
    }) => (
        rootNext({
            loading,
            userLevel,
            handleRowDelete : async ({legacy, proofOrder, order, order: {idCart, idOrder}}) => {
                if (await confirm({message: "Cancel approval submission"})) {
                    try {
                        if (legacy) {
                            await apis.next.approval.denyLegacyOrder(idOrder);
                        } else if (proofOrder) {
                            if(idCart) {
                                await apis.next.approval.denyProofOrder({cartId: idCart})
                            } else {
                                await apis.next.approval.denyProofOrder({itemId: idOrder})
                            }
                        } else {
                            if (!approvalRequests.find(row => row.order.idOrder !== idOrder && row.order.idCart === idCart)) {
                                await apis.next.approval.denyCcCart(idCart);
                            } else {
                                await apis.next.approval.denyCcOrder({cartId: idCart, orderId: idOrder});
                            }
                        }
                        setApprovalRequests(approvalRequests.filter(row => row.order.idOrder !== idOrder));
                        onSubmissionCancelled?.(order);
                        alert({message: "Approval submission has been cancelled and moved back to pending orders"});
                    } catch (error) {
                        console.error(error);
                        alert({message: "Error cancelling submission"});
                    }
                }
            },
            rows            : approvalRequests?.filter(makeRowFilter({searchQuery})) ?? [],
        })
    ))
    .run()
);

const flattenApprovalRequests = (approvalRequests:Types.Apis.Payloads.Next.OrderApproval.GetAllApprovalRequests.Response): (
    Array<Row>
) => [
    ...cascadeMap(approvalRequests, "[*].cartLevelApproval[*].orders[*]", (order, {0: approvalType, 2: idCart, 4: idOrder}) => ({
        approvalType,
        legacy : false,
        proofOrder: false,
        order  : {
            ...order,
            idCart  : parseInt(idCart),
            idOrder : parseInt(idOrder),
        },
        cart   : approvalRequests[approvalType].cartLevelApproval[idCart],
    })),
    ...cascadeMap(approvalRequests, "[*].orderLevelApproval[*]", (order, {0: approvalType, 2: idOrder}) => ({
        approvalType,
        legacy : true,
        proofOrder: false,
        order  : {...order, idOrder: parseInt(idOrder)},
    })),
    ...cascadeMap(approvalRequests, "[*].proofLevelApproval[*]", (order, {0: approvalType, 2: idOrder}) => ({
        approvalType,
        legacy : false,
        proofOrder: true,
        order  : {
            ...order,
            idCart  : order.idCart ? order.idCart : false,
            idOrder : parseInt(idOrder),
        }
    })),
];

const makeColumns = ({onRowDelete: handleRowDelete, userLevel}:{
    onRowDelete : (row:Row) => void,
    userLevel   : Types.Apis.Models.UserLevel,
}): Array<{
    className ?: string,
    label      : React.ReactNode,
    sortId    ?: string,
    renderRow  : (row:Row) => React.ReactNode,
    width     ?: string,
    xdQaName   : string,
}> => {
    const columns:ReturnType<typeof makeColumns> = [
        {
            label     : "STATUS",
            renderRow : () => "In Review",
            xdQaName  : "Status",
        },
        {
            label     : "ORDER NAME",
            renderRow : ({order}:Row) => (
                order.orderName
                || order.templateName
                || order.orderType
                || "Brochure"
            ),
            width     : "30%",
            xdQaName  : "Order Name",
        },
        {
            label     : "ORDER ID",
            renderRow : (row) => row.order.idOrder,
            xdQaName  : "Order Id",
        },
        {
            label     : "CART ID",
            sortId    : "cartId",
            renderRow : (row) => row.order.idCart,
            xdQaName  : "Card Id",
        },
        {
            label     : "TYPE",
            renderRow : ({order}:Row) => order.programType === "MAIN" ? order.orderType : order.programType?.toUpperCase(),
            xdQaName  : "Type",
        },
        (userLevel < AGENT_LEVEL) && {
            label     : "USER",
            sortId    : "agentName",
            renderRow : ({order}) => order.agentName,
            width     : "100px",
            xdQaName  : "User",
        },
        {
            label     : "DATE",
            sortId    : "date",
            renderRow : ({order}) => order.dateCreated,
            width     : "100px",
            xdQaName  : "Date",
        },
        {
            className : "centered",
            label     : <>DELETE <FontAwesomeIcon icon={faCircleInfo} title={DELETE_INFO}/></>,
            renderRow : (row) => (
                <img className="delete-icon"
                    src={require("../../../../../../public/assets/trash2.svg")}
                    alt="Delete"
                    onClick={() => handleRowDelete(row)}
                    xd-qa-type="icon"
                    xd-qa-name="Trash"
                />
            ),
            width     : "80px",
            xdQaName  : "Delete",
        },
    ];
    return columns.filter(Boolean);
}

const makeRowFilter = ({searchQuery}:{searchQuery: string}) => (
    chain(
        searchQuery.toLowerCase().replace(/\s+/g, " "),
        (searchQueryLc) => (row:Row) => [
            row.order.idOrder.toString(),
            row.order.idCart?.toString(),
            row.order.idAgent,
            row.order.idBranch,
            row.order.orderName,
            row.order.agentName,
            row.order.branchName,
            row.order.dateCreated,
        ].filter(Boolean).some((text) => chain(text.toLowerCase().replace(/\s+/g, " "),
            (textLc) => textLc.includes(searchQueryLc) || searchQueryLc.includes(textLc)
        ))
    )
);

